import type { ReactNode, Ref } from "react";
import { Knopf } from "./knopf";
import { SymbolEntfernen } from "./zeilen-symbole";

/**
 * Verknüpfungszeile (Musterkatalog M-011 Teil 3, Design-Guide `verknuepfungs-liste.md`): EINE Zeilenform für alle Arten
 * (Aufgabe, Konfigurationselement, Risiko, Besprechung, Wiki). Typ-Symbol im Rahmen, Titel (Link, wenn das Ziel eine
 * Adresse hat), darunter die Art („Abhängig von · Erledigt“, „Risiko“, „Wiki · Qualität“), rechts die Blockade-Pille
 * (`Zustandspille`, vom Aufrufer als `pille` eingehängt) und das ✕ zum Entfernen. Ohne `onEntfernen` (Beobachter) gibt
 * es kein ✕. Die Rückfrage vor dem Entfernen steht über `unten` in der Zeile (wie Teilaufgaben 4A).
 *
 * Der Baustein prüft keine Rechte: Der Aufrufer übergibt `onEntfernen` nur mit Schreibrecht, die Action prüft selbst
 * (r109); unlesbare Ziele liefert der Lader gar nicht erst (r110).
 */
const SYMBOL_KASTEN =
  "shrink-0 inline-flex size-8 items-center justify-center rounded-lg border border-layer-line bg-layer text-muted-foreground-1";

export interface VerknuepfungsZeileProps {
  titel: string;
  /** Adresse des Ziels; ohne sie steht der Titel als Text. */
  href?: string | null;
  /** Typ-Symbol (Lucide, vom Aufrufer nach Art gewählt). */
  symbol: ReactNode;
  /** Zweite Zeile: die Art, ggf. mit Zusatz („Abhängig von · Erledigt“). */
  art: string;
  /** Pille hinter dem Titel (Blockade, `Zustandspille`). */
  pille?: ReactNode;
  onEntfernen?: () => void;
  belegt?: boolean;
  /** Ref auf das ✕ (Fokus-Rückkehr nach Abbrechen). */
  entfernenRef?: Ref<HTMLButtonElement>;
  fehler?: ReactNode;
  /** Bereich unter der Zeile: Rückfrage vor dem Entfernen. */
  unten?: ReactNode;
}

export function VerknuepfungsZeile({ titel, href, symbol, art, pille, onEntfernen, belegt = false, entfernenRef, fehler, unten }: VerknuepfungsZeileProps) {
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/icon-action-list-with-dashed-dividers */}
      {/* Abweichung AW-014: flache Zeile mit gestricheltem Trenner (wie Vorschau B1) statt Karte je Eintrag; Titel als Link font-medium text-primary; Symbol im Rahmen size-8; ✕ immer sichtbar statt hover-reveal; natives title statt Tooltip-Baustein */}
      <li className="py-2.5 first:pt-0 border-t border-dashed border-line-2 first:border-t-0" aria-busy={belegt || undefined}>
        <div className="flex items-center gap-x-3">
          <span className={SYMBOL_KASTEN}>{symbol}</span>
          <div className="min-w-0 grow">
            {href ? (
              <a className="block truncate text-sm font-medium text-primary hover:text-primary-hover hover:underline focus:outline-hidden focus-visible:underline" href={href}>
                {titel}
              </a>
            ) : (
              <p className="truncate text-sm font-medium text-foreground">{titel}</p>
            )}
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground-1">
              <span>{art}</span>
              {pille}
            </p>
          </div>
          {onEntfernen && (
            <Knopf
              ref={entfernenRef}
              variante="ghost"
              nurSymbol
              groesse="sm"
              starr
              disabled={belegt}
              onClick={onEntfernen}
              label={`Verknüpfung entfernen: ${titel}`}
              titel="Entfernen"
              symbol={<SymbolEntfernen />}
            />
          )}
        </div>
        {fehler}
        {unten}
      </li>
    </>
  );
}
